import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Users, Activity, HelpCircle, Stethoscope, FileQuestion, BarChart3, Package } from 'lucide-react';
import { Card } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MedicalDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: t('totalPatients'), value: stats?.total_patients || 0, icon: Users, color: 'text-blue-500' },
    { label: t('totalDiagnoses'), value: stats?.total_diagnoses || 0, icon: Activity, color: 'text-green-500' },
    { label: t('questions'), value: stats?.total_questions || 0, icon: HelpCircle, color: 'text-purple-500' },
    { label: t('symptoms'), value: stats?.total_symptoms || 0, icon: Stethoscope, color: 'text-orange-500' },
    { label: t('conditions'), value: stats?.total_conditions || 0, icon: FileQuestion, color: 'text-pink-500' },
    ...(isAdmin ? [{ label: t('products'), value: stats?.total_products || 0, icon: Package, color: 'text-cyan-500' }] : []),
  ];

  const quickActions = isAdmin ? [
    { icon: Package, title: t('manageProducts'), description: t('manageProductsDesc'), path: '/admin/products' },
    { icon: HelpCircle, title: t('manageQuestions'), description: t('manageQuestionsDesc'), path: '/admin/questions' },
    { icon: Stethoscope, title: t('manageSymptoms'), description: t('manageSymptomsDesc'), path: '/admin/symptoms' },
  ] : [
    { icon: HelpCircle, title: t('manageQuestions'), description: t('manageQuestionsDesc'), path: '/medical/questions' },
    { icon: Stethoscope, title: t('manageSymptoms'), description: t('manageSymptomsDesc'), path: '/medical/symptoms' },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="medical-dashboard">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <span className="label-medical">{isAdmin ? t('adminDashboard') : t('medicalDashboard')}</span>
        <h1 className="text-3xl font-bold mt-2">{isAdmin ? t('welcomeAdmin') : t('welcomeDoctor')} {user?.name}</h1>
        <p className="text-muted-foreground mt-1">
          {isAdmin ? t('manageProductsAndAnalytics') : t('manageDiagnosticQuestions')}
        </p>
      </motion.div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
        {loading ? (
          Array(isAdmin ? 6 : 5).fill(0).map((_, i) => (
            <Card key={i} className="p-5 rounded-2xl animate-pulse">
              <div className="h-10 bg-muted rounded mb-3" />
              <div className="h-6 bg-muted rounded w-1/2" />
            </Card>
          ))
        ) : (
          statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="p-5 rounded-2xl card-hover" data-testid={`stat-${index}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl bg-muted flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </div>
                <div className="text-3xl font-bold">{stat.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Quick actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="mt-10"
      >
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          {t('quickActions')}
        </h2>
        <div className={`grid gap-4 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
          {quickActions.map((action, index) => (
            <Card 
              key={action.path}
              className="p-6 rounded-2xl border-2 border-transparent hover:border-primary/50 transition-all cursor-pointer card-hover"
              onClick={() => navigate(action.path)}
              data-testid={`action-${index}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <action.icon className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold">{action.title}</h3>
                  <p className="text-muted-foreground text-sm">{action.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </motion.div>
    </div>
  );
};
